/*
 * @Author: chenchaoqiang@leedarson.com
 * @Date: 2022-08-09 15:27:10
 * Copyright 2022 Leedarson, All Rights Reserved.
 */
import { useState, useCallback, useEffect } from 'react';
import { packageApi } from '@api';
import { IPackagePrice, IPackage } from '@root/types/package';
import { IOrderCreateData } from '@root/types/order';
import { PAY_ENTER } from '@root/constant';
import { useCurrentHouseId } from '../useHouseList';
import usePayPalResult from '../usePayPal';
import { getQueryString } from '@root/shared';
import Toast from '@root/components/toast';
import { useTranslation } from 'react-i18next';
import { useUnBindDevicesAction } from '../useUnBindDevices';
import { getOrderOriginal } from '@root/shared/referrerBuried';

interface ICreateOrderProps {
  packageInfo: IPackage;
  priceInfo?: IPackagePrice;
  deviceIds?: string[];
  payMode?: number;
}

interface IProps {
  isPromotions?: boolean;
}

const useCreateOrder = (props?: IProps) => {
  const { isPromotions } = props || {};
  const homeId = useCurrentHouseId();
  const { t } = useTranslation();
  const [paying, setPaying] = useState(false);
  const { getUnBindDevices } = useUnBindDevicesAction();
  const { getPayUrl } = usePayPalResult({ setPaying });
  // 只有变更套餐的时候才会有这个 id
  const changePackageId = getQueryString('changePackageId');
  const originalRemark = getQueryString('sensor') || '';
  const activityId = getQueryString('activityId') || '';

  // 没有可选设备时默认订阅一份
  const onCreateOrder = useCallback(
    ({ packageInfo, priceInfo, deviceIds, payMode }: ICreateOrderProps) => {
      setPaying(true);

      Toast.info(t('create_order'), 0);

      const { checkedPriceId, goodsPriceList, supportRenew } = packageInfo;
      const { price, currency, goodsId, goodsPriceId, buyNum } =
        priceInfo ||
        goodsPriceList?.find((i) => i.goodsPriceId === checkedPriceId) ||
        ({} as IPackagePrice);

      const payPrice = price ? Number(price).toFixed(2) : '';
      const _data: IOrderCreateData = {
        discountRate: 0,
        currency,
        homeId,
        deviceIds,
        // 只有变更套餐的时候才会有这个 id
        packageId: changePackageId || undefined,
        activityId: activityId || undefined,
        payEnter: sessionStorage.getItem(PAY_ENTER) || undefined,
        goodsReqs: [
          {
            goodsId,
            goodsNum: buyNum || 1,
            goodsType: 5,
            goodsPriceId,
          },
        ],
        orderPrice: payPrice,
        payPrice,
        preferentialPrice: 0,
        original: getOrderOriginal(),
        originalRemark: isPromotions ? 'promotions' : originalRemark,
      };

      packageApi
        .createOrder(_data)
        .then((data: any) => {
          if (deviceIds?.length) {
            getUnBindDevices();
          }

          let _urls: any = {};

          if (isPromotions) {
            const _baseUrl = `${window.location.origin}${process.env.REACT_APP_ROUTE_BASE_NAME}`;
            _urls = {
              cancelUrl: `${_baseUrl}/promotions`,
              errorUrl: `${_baseUrl}/payFailed/promotions`,
              successUrl: `${_baseUrl}/paySuccess/promotions`,
            };
          }
          getPayUrl({ orderId: data, type: supportRenew, payMode, ..._urls });
        })
        .catch((reason) => {
          Toast.destroy();
          setPaying(false);
          const status = reason.response?.status;
          const data = reason.response?.data || {};
          let msg = t('create_order_error');
          if (status === 400 && data.desc) {
            msg = data.desc;
          }
          Toast.info(msg);
        });
    },
    [
      activityId,
      changePackageId,
      getPayUrl,
      getUnBindDevices,
      homeId,
      isPromotions,
      originalRemark,
      t,
    ],
  );

  useEffect(() => {
    return () => {
      Toast.destroy();
    };
  }, []);

  return {
    paying,
    onCreateOrder,
  };
};

export default useCreateOrder;
